import { usePermissions } from "@/context/permissions-context"
import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query"

interface ProtectedQueryOptions<TData, TError> extends Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> {
  queryKey: unknown[]
  queryFn: () => Promise<TData>
  requiredPermissions?: string[]
  requireAll?: boolean
}

export function useProtectedQuery<TData = unknown, TError = unknown>({
  queryKey,
  queryFn,
  requiredPermissions = [],
  requireAll = true,
  ...options
}: ProtectedQueryOptions<TData, TError>): UseQueryResult<TData, TError> & { hasPermission: boolean } {
  const { hasAllPermissions, hasAnyPermission } = usePermissions()

  const hasPermission = requiredPermissions.length === 0 
    ? true 
    : requireAll 
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions)

  const result = useQuery<TData, TError>({
    ...options,
    queryKey,
    queryFn,
    // Apply after spread so caller's enabled cannot override permission check
    enabled: hasPermission && (options.enabled ?? true),
  })

  return {
    ...result,
    hasPermission,
  }
}